import { useEffect, useId, useRef } from 'react'
import { createPortal } from 'react-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/Button'
import { useScrollLock } from '@/lib/useScrollLock'
import { cn } from '@/lib/utils'

export type ConfirmTone = 'destructive' | 'default'

/** Everything inside the dialog a keyboard user can land on (§16 focus trap). */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * Elements that already translate a bare `Enter` into their own activation
 * (a synthetic `click` for buttons/links, submit for fields). §57: the dialog
 * must not also act on that same keypress — that is how Enter-on-*Cancel*
 * used to run cancel **and** confirm together.
 */
const SELF_ACTIVATING_SELECTOR = 'button, a, input, textarea, select'

interface ConfirmDialogProps {
  open: boolean
  title: string
  description?: React.ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: ConfirmTone
  loading?: boolean
  onConfirm: () => void
  onCancel: () => void
  /** Optional third action rendered between cancel and confirm — e.g. a
   *  de-emphasized "Discard and exit" next to a primary "Save and exit". */
  altLabel?: string
  altTone?: ConfirmTone
  onAlt?: () => void
}

export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  loading = false,
  onConfirm,
  onCancel,
  altLabel,
  altTone = 'default',
  onAlt,
}: ConfirmDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const cancelRef = useRef<HTMLButtonElement>(null)
  /** Whatever had focus before we opened, so we can hand it back on close. */
  const restoreRef = useRef<HTMLElement | null>(null)
  // Unique per mounted instance so two simultaneously-open dialogs (e.g. a
  // closing dialog mid AnimatePresence exit overlapping an opening sibling)
  // never emit the same aria-labelledby/aria-describedby target (§ id bug).
  const uid = useId()
  const titleId = `${uid}-title`
  const descId = `${uid}-desc`

  // Lock background scroll while open. Scrollbar lane stays reserved via
  // `html { scrollbar-gutter: stable }` in index.css → no layout shift.
  useScrollLock(open)

  // Focus restore (§16). Captured on open, handed back on close/unmount —
  // keyed on `open` alone so a re-render for `loading` can never re-capture
  // a node that lives *inside* the dialog and is about to be destroyed.
  useEffect(() => {
    if (!open) return
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null
    return () => {
      const previous = restoreRef.current
      restoreRef.current = null
      // The trigger may have unmounted while the dialog was open (deleting a
      // row removes the row's own menu button) — `isConnected` guards that.
      if (previous?.isConnected && typeof previous.focus === 'function') {
        previous.focus()
      }
    }
  }, [open])

  // Safe autofocus (§57). A confirmation is a deliberate second act, so a
  // destructive dialog opens with *Cancel* focused, never the red button.
  useEffect(() => {
    if (!open) return
    const initial = tone === 'destructive' ? cancelRef.current : confirmRef.current
    // Fall back to the dialog itself so focus is never left outside it.
    ;(initial ?? dialogRef.current)?.focus()
  }, [open, tone])

  // Key handling is scoped to the dialog node (§57) — never `window`, which
  // fired alongside the focused button's own Enter activation.
  useEffect(() => {
    const node = dialogRef.current
    if (!open || !node) return

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Escape dismisses even while `loading`. Asymmetric on purpose: a
        // caller's confirm handler that hangs or rejects without clearing its
        // flag must never leave the dialog unclosable, and 18 of the 19 call
        // sites are destructive. The backdrop click and the Cancel button stay
        // guarded by `!loading` — reaching for Escape is a deliberate two-hand
        // act, a stray click on the backdrop mid-operation is not.
        // We own dismissal while we're the top layer: don't also close the
        // modal or drawer that opened us.
        e.stopPropagation()
        onCancel()
        return
      }

      if (e.key === 'Tab') {
        const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
        // Tab never leaves the dialog. Mid-submit only Cancel and the alt
        // action go natively disabled — the loading Confirm keeps its place in
        // `focusables` (it is aria-disabled, not disabled), so Tab parks there.
        // The empty case is the defensive floor: swallow the key regardless so
        // the trap holds even with nothing left to land on.
        e.preventDefault()
        e.stopPropagation()
        if (focusables.length === 0) return
        const current = focusables.indexOf(document.activeElement as HTMLElement)
        let next: HTMLElement | undefined
        if (current === -1) {
          // Focus is inside the dialog but not on a tabbable node — the dialog
          // element itself (`tabIndex={-1}`), or something a caller's handler
          // focused. Restart the trap at the end Tab direction implies, rather
          // than letting -1 arithmetic arrive there by coincidence.
          next = e.shiftKey ? focusables[focusables.length - 1] : focusables[0]
        } else {
          // Normal case: step one, wrapping around at either end.
          next = e.shiftKey
            ? focusables[(current === 0 ? focusables.length : current) - 1]
            : focusables[(current + 1) % focusables.length]
        }
        next?.focus()
        return
      }

      if (e.key === 'Enter') {
        if (loading) return
        // Focus is already on something that handles Enter itself — let it.
        // Only the bare-Enter-with-nothing-focused convenience path confirms.
        if ((e.target as HTMLElement | null)?.closest(SELF_ACTIVATING_SELECTOR)) return
        e.stopPropagation()
        onConfirm()
      }
    }

    node.addEventListener('keydown', onKey)
    return () => {
      node.removeEventListener('keydown', onKey)
    }
  }, [open, loading, onCancel, onConfirm])

  if (typeof document === 'undefined') return null

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md">
          {/* Backdrop — blur-only, no dark overlay (§16, §29) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed top-0 left-0 w-screen h-screen"
            onClick={() => !loading && onCancel()}
            aria-hidden="true"
          />

          <motion.div
            ref={dialogRef}
            role="alertdialog"
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={description ? descId : undefined}
            // Focusable-but-not-tabbable: clicking dead space inside the
            // dialog lands here instead of on <body>, so the scoped keydown
            // listener above keeps seeing Escape/Tab.
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 4 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'relative w-full max-w-md rounded-2xl border bg-card p-6 shadow-2xl outline-none',
              'border-border/60 dark:border-white/[0.08] dark:bg-[#191a1b]',
            )}
          >
            <h2
              id={titleId}
              className="text-lg font-emphasis tracking-tight text-foreground dark:text-[#f7f8f8]"
            >
              {title}
            </h2>
            {description && (
              <div
                id={descId}
                className="mt-2 text-sm leading-relaxed text-muted-foreground dark:text-[#d0d6e0]"
              >
                {description}
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <Button
                ref={cancelRef}
                variant="outline"
                onClick={onCancel}
                disabled={loading}
              >
                {cancelLabel}
              </Button>
              {altLabel && onAlt && (
                <Button
                  variant="ghost"
                  className={cn(
                    altTone === 'destructive' &&
                      'text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300',
                  )}
                  onClick={onAlt}
                  disabled={loading}
                >
                  {altLabel}
                </Button>
              )}
              <Button
                ref={confirmRef}
                variant={tone === 'destructive' ? 'destructive' : 'default'}
                onClick={onConfirm}
                loading={loading}
              >
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}

export default ConfirmDialog
